import { Head, router } from "@inertiajs/react"
import { useState } from "react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Manage", href: "/admin/retention" },
  { title: "Housekeeping", href: "/admin/housekeeping" },
]

interface PurgeCase {
  id: number
  case_number: string | null
  hpi_name: string
  status: number
  open_date: string | null
  closed_date: string | null
  subject_count: number
  is_deleted: boolean
}

interface RestrictedPurgeCase extends PurgeCase {
  update_count: number
}

interface PreviewSubject {
  id: number
  name: string
  has_ssn: boolean
  has_dob: boolean
}

interface PreviewLog {
  id: number
  date: string | null
  content: string | null
}

interface PreviewData {
  case_id: number
  case_name: string
  subjects: PreviewSubject[]
  restricted_logs?: PreviewLog[]
}

type PreviewType = "sensitive" | "restricted"

interface Props {
  sensitive_list: PurgeCase[]
  restricted_list: RestrictedPurgeCase[]
  flash_message: string | null
  flash_error: string | null
}

export default function Index({
  sensitive_list,
  restricted_list,
  flash_message,
  flash_error,
}: Props) {
  const [preview, setPreview] = useState<PreviewData | null>(null)
  const [previewType, setPreviewType] = useState<PreviewType>("sensitive")
  const [loadingPreview, setLoadingPreview] = useState<number | null>(null)
  const [purging, setPurging] = useState<number | null>(null)

  const handlePreview = async (caseId: number, type: PreviewType) => {
    setLoadingPreview(caseId)
    try {
      const endpoint =
        type === "sensitive" ? "preview_sensitive" : "preview_restricted"
      const response = await fetch(`/admin/housekeeping/${caseId}/${endpoint}`)
      const data = (await response.json()) as PreviewData
      setPreviewType(type)
      setPreview(data)
    } finally {
      setLoadingPreview(null)
    }
  }

  const handlePurge = (caseId: number, caseName: string, type: PreviewType) => {
    const label = type === "sensitive" ? "sensitive" : "restricted"
    if (
      !confirm(
        `Are you sure you want to purge ${label} data for case "${caseName}"? This action cannot be undone.`,
      )
    ) {
      return
    }
    setPurging(caseId)
    const endpoint =
      type === "sensitive" ? "purge_sensitive" : "purge_restricted"
    router.post(`/admin/housekeeping/${caseId}/${endpoint}`)
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Housekeeping" />

      <div className="p-6">
        <h1 className="border-hpi-light-gray mb-6 border-b pb-2 text-2xl font-semibold">
          Housekeeping
        </h1>

        {flash_message && (
          <div className="mb-4 rounded bg-green-100 p-3 text-green-800">
            {flash_message}
          </div>
        )}
        {flash_error && (
          <div className="mb-4 rounded bg-red-100 p-3 text-red-800">
            {flash_error}
          </div>
        )}

        <section className="mb-8">
          <h2 className="mb-3 text-lg font-semibold">
            Purge Sensitive Info &mdash; 90 days after case closed
          </h2>

          {sensitive_list.length > 0 ? (
            <table className="border-hpi-light-gray w-full border-collapse border">
              <thead>
                <tr className="bg-hpi-pale-gray">
                  <th className="border-hpi-light-gray border px-4 py-2 text-left">
                    Case
                  </th>
                  <th className="border-hpi-light-gray border px-4 py-2 text-left">
                    Opened
                  </th>
                  <th className="border-hpi-light-gray border px-4 py-2 text-left">
                    Closed
                  </th>
                  <th className="border-hpi-light-gray border px-4 py-2 text-left">
                    Subjects
                  </th>
                  <th className="border-hpi-light-gray border px-4 py-2 text-left">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {sensitive_list.map((c) => (
                  <tr key={c.id} className="hover:bg-hpi-pale-blue">
                    <td className="border-hpi-light-gray border px-4 py-2">
                      <a
                        href={`/cases/${c.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-hpi-active-blue hover:underline"
                      >
                        {c.hpi_name}
                      </a>
                      {c.is_deleted && (
                        <span
                          className="text-hpi-red ml-2"
                          title="Deleted case"
                        >
                          &#x1f5d1;
                        </span>
                      )}
                    </td>
                    <td className="border-hpi-light-gray border px-4 py-2">
                      {c.open_date}
                    </td>
                    <td className="border-hpi-light-gray border px-4 py-2">
                      {c.closed_date}
                    </td>
                    <td className="border-hpi-light-gray border px-4 py-2">
                      {c.subject_count > 0 ? c.subject_count : ""}
                    </td>
                    <td className="border-hpi-light-gray border px-4 py-2">
                      <button
                        onClick={() => void handlePreview(c.id, "sensitive")}
                        disabled={loadingPreview === c.id}
                        className="text-hpi-active-blue mr-3 hover:underline disabled:opacity-50"
                      >
                        {loadingPreview === c.id ? "Loading..." : "Preview"}
                      </button>
                      <button
                        onClick={() =>
                          handlePurge(c.id, c.hpi_name, "sensitive")
                        }
                        disabled={purging === c.id}
                        className="text-hpi-red hover:underline disabled:opacity-50"
                      >
                        {purging === c.id ? "Purging..." : "Purge"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-hpi-med-gray">
              No cases are currently eligible for sensitive data purge.
            </p>
          )}
        </section>

        <section className="mb-8">
          <h2 className="mb-3 text-lg font-semibold">
            Purge Restricted Notes &mdash; 1 year after case closed
            <span className="ml-2 text-sm font-normal text-gray-500">
              Purge Subject Sensitive Data
            </span>
          </h2>

          {restricted_list.length > 0 ? (
            <table className="border-hpi-light-gray w-full border-collapse border">
              <thead>
                <tr className="bg-hpi-pale-gray">
                  <th className="border-hpi-light-gray border px-4 py-2 text-left">
                    Case
                  </th>
                  <th className="border-hpi-light-gray border px-4 py-2 text-left">
                    Opened
                  </th>
                  <th className="border-hpi-light-gray border px-4 py-2 text-left">
                    Closed
                  </th>
                  <th className="border-hpi-light-gray border px-4 py-2 text-left">
                    Sub
                  </th>
                  <th className="border-hpi-light-gray border px-4 py-2 text-left">
                    Upd
                  </th>
                  <th className="border-hpi-light-gray border px-4 py-2 text-left">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {restricted_list.map((c) => (
                  <tr key={c.id} className="hover:bg-hpi-pale-blue">
                    <td className="border-hpi-light-gray border px-4 py-2">
                      <a
                        href={`/cases/${c.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-hpi-active-blue hover:underline"
                      >
                        {c.hpi_name}
                      </a>
                      {c.is_deleted && (
                        <span
                          className="text-hpi-red ml-2"
                          title="Deleted case"
                        >
                          &#x1f5d1;
                        </span>
                      )}
                    </td>
                    <td className="border-hpi-light-gray border px-4 py-2">
                      {c.open_date}
                    </td>
                    <td className="border-hpi-light-gray border px-4 py-2">
                      {c.closed_date}
                    </td>
                    <td className="border-hpi-light-gray border px-4 py-2">
                      {c.subject_count > 0 ? c.subject_count : ""}
                    </td>
                    <td className="border-hpi-light-gray border px-4 py-2">
                      {c.update_count > 0 ? c.update_count : ""}
                    </td>
                    <td className="border-hpi-light-gray border px-4 py-2">
                      <button
                        onClick={() => void handlePreview(c.id, "restricted")}
                        disabled={loadingPreview === c.id}
                        className="text-hpi-active-blue mr-3 hover:underline disabled:opacity-50"
                      >
                        {loadingPreview === c.id ? "Loading..." : "Preview"}
                      </button>
                      <button
                        onClick={() =>
                          handlePurge(c.id, c.hpi_name, "restricted")
                        }
                        disabled={purging === c.id}
                        className="text-hpi-red hover:underline disabled:opacity-50"
                      >
                        {purging === c.id ? "Purging..." : "Purge"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-hpi-med-gray">
              No cases are currently eligible for restricted notes purge.
            </p>
          )}
        </section>

        {preview && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="max-h-[80vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold">
                  Preview: {preview.case_name}
                </h3>
                <button
                  onClick={() => setPreview(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  &times;
                </button>
              </div>

              {preview.subjects.length > 0 && (
                <>
                  <h4 className="mb-2 font-medium">
                    Subjects with Sensitive Data
                  </h4>
                  <table className="border-hpi-light-gray mb-4 w-full border-collapse border">
                    <thead>
                      <tr className="bg-hpi-pale-gray">
                        <th className="border-hpi-light-gray border px-3 py-1 text-left">
                          Subject
                        </th>
                        <th className="border-hpi-light-gray border px-3 py-1 text-left">
                          SSN
                        </th>
                        <th className="border-hpi-light-gray border px-3 py-1 text-left">
                          DOB
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.subjects.map((s) => (
                        <tr key={s.id}>
                          <td className="border-hpi-light-gray border px-3 py-1">
                            {s.name}
                          </td>
                          <td className="border-hpi-light-gray border px-3 py-1">
                            {s.has_ssn ? "Yes" : ""}
                          </td>
                          <td className="border-hpi-light-gray border px-3 py-1">
                            {s.has_dob ? "Yes" : ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}

              {previewType === "restricted" &&
                preview.restricted_logs &&
                preview.restricted_logs.length > 0 && (
                  <>
                    <h4 className="mb-2 font-medium">Restricted Log Entries</h4>
                    <table className="border-hpi-light-gray mb-4 w-full border-collapse border">
                      <thead>
                        <tr className="bg-hpi-pale-gray">
                          <th className="border-hpi-light-gray border px-3 py-1 text-left">
                            Date
                          </th>
                          <th className="border-hpi-light-gray border px-3 py-1 text-left">
                            Content
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.restricted_logs.map((l) => (
                          <tr key={l.id}>
                            <td className="border-hpi-light-gray border px-3 py-1 whitespace-nowrap">
                              {l.date}
                            </td>
                            <td className="border-hpi-light-gray border px-3 py-1">
                              {l.content}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}

              {preview.subjects.length === 0 &&
                (!preview.restricted_logs ||
                  preview.restricted_logs.length === 0) && (
                  <p className="text-hpi-med-gray">No data found to preview.</p>
                )}

              <div className="mt-4 flex justify-end gap-3">
                <button
                  onClick={() => setPreview(null)}
                  className="rounded border px-4 py-2 hover:bg-gray-50"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    const type = previewType
                    const caseId = preview.case_id
                    const caseName = preview.case_name
                    setPreview(null)
                    handlePurge(caseId, caseName, type)
                  }}
                  className="bg-hpi-red rounded px-4 py-2 text-white hover:opacity-90"
                >
                  Purge
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  )
}
